// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Composable: useAuth
 *
 * OIDC authentication composable built on oidc-client-ts UserManager.
 * Gates access to the system-admin SPA and exposes `getAccessToken()` for
 * Bearer-authenticated calls to the system-admin BFF (#254).
 *
 * Configuration is injected at deploy time via api_endpoints.js as
 * window.AUTH0_DOMAIN / AUTH0_CLIENT_ID / AUTH0_AUDIENCE. When
 * AUTH0_DOMAIN is missing (local dev), the auth plugin is a no-op and
 * isAuthenticated stays true so the UI is usable unauthenticated.
 */

import { ref } from 'vue';
import { UserManager } from 'oidc-client-ts';

let userManager = null;
let authEnabled = false;

const isAuthenticated = ref(false);
const isLoading = ref(true);
const currentUser = ref(null);

function toUserProfile(user) {
  if (!user || !user.profile) return null;
  return {
    sub: user.profile.sub,
    email: user.profile.email,
    name: user.profile.name,
  };
}

function syncState(user) {
  const valid = user !== null && !user.expired;
  isAuthenticated.value = valid;
  currentUser.value = valid ? toUserProfile(user) : null;
}

function buildSettings(config) {
  const settings = {
    authority: config.authority,
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: config.redirectUri.replace(/\/callback$/, '/'),
    response_type: 'code',
    scope: config.scope || 'openid profile email',
    automaticSilentRenew: true,
    silent_redirect_uri: config.redirectUri,
  };
  // Auth0-specific extras (audience, organization). The OIDC core spec
  // doesn't define these; oidc-client-ts forwards them verbatim to
  // /authorize via extraQueryParams.
  const extras = {};
  if (config.audience) extras.audience = config.audience;
  if (config.organization) extras.organization = config.organization;
  if (Object.keys(extras).length > 0) {
    settings.extraQueryParams = extras;
  }
  return settings;
}

async function initSession() {
  if (!userManager) return;
  try {
    const user = await userManager.getUser();
    if (user && !user.expired) {
      syncState(user);
    } else {
      try {
        const renewed = await userManager.signinSilent();
        syncState(renewed);
      } catch {
        syncState(null);
      }
    }
  } catch {
    syncState(null);
  } finally {
    isLoading.value = false;
  }
}

export async function handleCallback() {
  if (!userManager) return false;
  try {
    const user = await userManager.signinCallback();
    syncState(user || null);
    return true;
  } catch (err) {
    console.error('[useAuth] callback error', err);
    syncState(null);
    return false;
  }
}

export function useAuth() {
  async function login() {
    if (!userManager) {
      throw new Error('[useAuth] AuthPlugin not installed.');
    }
    await userManager.signinRedirect();
  }

  async function logout() {
    if (!userManager) {
      throw new Error('[useAuth] AuthPlugin not installed.');
    }
    syncState(null);
    await userManager.signoutRedirect();
  }

  /**
   * Resolve the current access_token, attempting silent renewal if needed.
   * Throws when the auth plugin is disabled or the user must re-login.
   * @returns {Promise<string>}
   */
  async function getAccessToken() {
    if (!userManager) {
      throw new Error('[useAuth] AuthPlugin not installed.');
    }
    let user = await userManager.getUser();
    if (!user || user.expired) {
      try {
        user = await userManager.signinSilent();
      } catch {
        throw new Error('[useAuth] Unable to obtain access token. User may need to log in.');
      }
    }
    if (!user || !user.access_token) {
      throw new Error('[useAuth] No access token available.');
    }
    return user.access_token;
  }

  return {
    isAuthenticated,
    isLoading,
    user: currentUser,
    authEnabled: () => authEnabled,
    login,
    logout,
    getAccessToken,
  };
}

export function createAuthPlugin(config) {
  return {
    install() {
      authEnabled = true;
      const settings = buildSettings(config);
      userManager = new UserManager(settings);
      userManager.events.addUserLoaded((u) => syncState(u));
      userManager.events.addUserUnloaded(() => syncState(null));
      userManager.events.addSilentRenewError(() => syncState(null));
      initSession();
    },
  };
}

/**
 * Disable the auth gate entirely. Used when AUTH0_DOMAIN is not provided
 * (e.g. local dev) so the UI renders without requiring login.
 */
export function disableAuthGate() {
  authEnabled = false;
  isAuthenticated.value = true;
  isLoading.value = false;
}
